import React, { useMemo } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_HEIGHT, CHART_INK, SCORE_AXIS_DOMAIN } from '../../config/dashboardTheme';
import { TrendChartRow, TrendLine } from './trendTypes';

interface TrendChartProps {
  lines: TrendLine[];
  bucketLabels: string[];
  emptyMessage: string;
  /** Small multiples pass a reduced height and share one legend between them. */
  height?: number;
  showLegend?: boolean;
}

const TEAM_LINE_WIDTH = 2.5;
const SERIES_LINE_WIDTH = 2;
const DOT_RADIUS = 3.5;
const ACTIVE_DOT_RADIUS = 5;
// No negative left margin: at -12 the widest tick ("100%") lost its first
// digit on every chart (owner, 2026-10-01).
const CHART_MARGIN = { top: 8, right: 16, left: 0, bottom: 0 } as const;

interface TooltipEntry {
  dataKey?: string | number;
  name?: string;
  color?: string;
  value?: number | null;
}

interface TrendTooltipProps {
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string | number;
}

function TrendTooltip({ active, payload, label }: TrendTooltipProps) {
  if (!active || !payload?.length) return null;

  const rows = payload
    .filter((entry) => entry?.value !== null && entry?.value !== undefined)
    .sort((a, b) => Number(b?.value ?? 0) - Number(a?.value ?? 0));

  if (rows.length === 0) return null;

  return (
    <div className="rounded-lg border border-hairline bg-surface px-3 py-2 shadow-md">
      <p className="mb-1 text-xs font-semibold text-ink">{label}</p>
      <div className="space-y-0.5">
        {rows.map((entry) => (
          <div key={String(entry?.dataKey)} className="flex items-center gap-2 text-xs">
            <span
              className="inline-block h-2 w-2 shrink-0 rounded-full"
              style={{ backgroundColor: entry?.color }}
            />
            <span className="text-ink-secondary">{entry?.name}</span>
            <span className="ml-auto font-semibold text-ink">
              {Math.round(Number(entry?.value))}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Plots one or more 0-100 score series over the same time buckets.
 *
 * Gaps are connected so a rep with occasional quiet weeks still reads as one
 * line; the dots mark the buckets that actually carry data.
 */
const TrendChart: React.FC<TrendChartProps> = ({
  lines,
  bucketLabels,
  emptyMessage,
  height = CHART_HEIGHT,
  showLegend = true,
}) => {
  const rows = useMemo<TrendChartRow[]>(
    () =>
      bucketLabels.map((bucketLabel, index) => {
        const row: TrendChartRow = { bucketLabel };
        lines.forEach((line) => {
          row[line.key] = line.values?.[index] ?? null;
        });
        return row;
      }),
    [bucketLabels, lines]
  );

  const hasAnyValue = lines.some((line) => line.values?.some((value) => value !== null));

  if (!hasAnyValue) {
    return (
      <div
        className="flex items-center justify-center text-sm text-ink-muted"
        style={{ height }}
      >
        {emptyMessage}
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={rows} margin={CHART_MARGIN}>
        <CartesianGrid stroke={CHART_INK.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="bucketLabel"
          tick={{ fill: CHART_INK.label, fontSize: 12 }}
          axisLine={{ stroke: CHART_INK.axis }}
          tickLine={false}
        />
        <YAxis
          domain={SCORE_AXIS_DOMAIN}
          tick={{ fill: CHART_INK.label, fontSize: 12 }}
          tickFormatter={(value) => `${value}%`}
          axisLine={false}
          tickLine={false}
          width={48}
        />
        <Tooltip content={<TrendTooltip />} />
        {showLegend && (
          <Legend
            verticalAlign="bottom"
            height={32}
            iconType="plainline"
            wrapperStyle={{ fontSize: 12, color: CHART_INK.label }}
          />
        )}
        {lines.map((line) => (
          <Line
            key={line.key}
            type="monotone"
            dataKey={line.key}
            name={line.label}
            stroke={line.color}
            strokeWidth={line.isTeamAverage ? TEAM_LINE_WIDTH : SERIES_LINE_WIDTH}
            strokeDasharray={line.isTeamAverage ? '6 3' : undefined}
            dot={{ r: DOT_RADIUS, strokeWidth: 0, fill: line.color }}
            activeDot={{ r: ACTIVE_DOT_RADIUS }}
            connectNulls
            isAnimationActive={false}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};

export default TrendChart;
